import { useState } from "react";
import type { SortKey } from "@/utils/movieUtils";

interface FilterPanelProps {
  region: string;
  year: string;
  genre: string;
  sort: SortKey;
  regionOptions: string[];
  yearOptions: string[];
  genreOptions: string[];
  onRegion: (v: string) => void;
  onYear: (v: string) => void;
  onGenre: (v: string) => void;
  onSort: (v: SortKey) => void;
  onReset: () => void;
}

interface RowProps {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}

function FilterRow({ label, options, value, onChange }: RowProps) {
  return (
    <div className="flex flex-col gap-2 py-2.5 md:flex-row md:items-center md:gap-4">
      <span className="w-14 shrink-0 text-sm font-medium text-foreground-500">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`rounded-md px-3 py-1.5 text-sm transition-colors whitespace-nowrap ${
              value === opt
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "latest", label: "最新", icon: "ri-time-line" },
  { key: "hot", label: "热门", icon: "ri-fire-line" },
  { key: "rating", label: "评分", icon: "ri-star-line" },
];

export default function FilterPanel({
  region,
  year,
  genre,
  sort,
  regionOptions,
  yearOptions,
  genreOptions,
  onRegion,
  onYear,
  onGenre,
  onSort,
  onReset,
}: FilterPanelProps) {
  const [openMobile, setOpenMobile] = useState(false);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <button
        type="button"
        onClick={() => setOpenMobile((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 md:hidden"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
          <i className="ri-filter-3-line" aria-hidden="true"></i>
          筛选与排序
        </span>
        <i
          className={openMobile ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}
          aria-hidden="true"
        ></i>
      </button>

      <div
        className={`${openMobile ? "block" : "hidden"} divide-y divide-background-100 px-4 pb-4 md:block md:divide-none md:p-5`}
      >
        <FilterRow
          label="地区"
          options={regionOptions}
          value={region}
          onChange={onRegion}
        />
        <FilterRow
          label="年份"
          options={yearOptions}
          value={year}
          onChange={onYear}
        />
        <FilterRow
          label="类型"
          options={genreOptions}
          value={genre}
          onChange={onGenre}
        />

        <div className="flex flex-col gap-2 py-2.5 md:flex-row md:items-center md:gap-4">
          <span className="w-14 shrink-0 text-sm font-medium text-foreground-500">
            排序
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => onSort(opt.key)}
                  className={`flex items-center gap-1 rounded-full px-3 py-1 text-sm transition-colors whitespace-nowrap ${
                    sort === opt.key
                      ? "bg-background-50 text-primary-600 shadow-sm"
                      : "text-foreground-500 hover:text-foreground-800"
                  }`}
                >
                  <i className={opt.icon} aria-hidden="true"></i>
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={onReset}
              className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 whitespace-nowrap"
            >
              <i className="ri-refresh-line" aria-hidden="true"></i>
              重置
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}